<script>
import { debounce } from 'debounce'
import store from '@state/store'
import EventBigCalendar from '@events/components/event-big-calendar.vue'
import EventDetails from '@events/components/event-details.vue'
import EventTile from '@events/components/event-tile.vue'
import { authComputed } from '@state/helpers'
import EventsParentMixin from '@events/mixins/events-parent-mixin'
import { isPast, isFuture, parseISO, addMonths } from 'date-fns'

export default {
  name: 'EventsOrganizationTab',
  components: { EventBigCalendar, EventTile, EventDetails },
  mixins: [EventsParentMixin],
  props: {
    tab: {
      type: String,
      default: '',
    },
    organizationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      eventsPage: 0,
      eventsPerPage: 12,
      singleEvent: false,
    }
  },
  computed: {
    organization() {
      return this.$store.state.organizations.organization || {}
    },
    allEvents() {
      const { name } = this.$route
      const events = this.$store.state.events.events || null

      if (!events) return

      if (name === 'organizationEvents') {
        return events.filter(({ end }) => isFuture(parseISO(end)))
      }

      if (name === 'myOrganizationEvents') {
        return events.filter(
          ({ user_id: userId, leaders, users }) =>
            userId === this.currentUserId ||
            !!leaders.find(({ id }) => id === this.currentUserId) ||
            !!users.find(({ id }) => id === this.currentUserId)
        )
      }

      if (name === 'pastOrganizationEvents') {
        return events.filter(({ end }) => isPast(parseISO(end)))
      }

      return events
    },
    reachedLastPage() {
      return this.maxEventsPages - this.eventsPage - 1 === 0
    },
    events() {
      const endIndex = (this.eventsPage + 1) * this.eventsPerPage
      return this.allEvents && this.allEvents.slice(0, endIndex)
    },
    maxEventsPages() {
      return Math.ceil(
        this.allEvents && this.allEvents.length / this.eventsPerPage
      )
    },
    ...authComputed,
  },
  methods: {
    currentDate() {
      return this.$store.state.events.filters.from_date || new Date()
    },
    nextPage() {
      if (!this.reachedLastPage) {
        this.eventsPage++
      }
    },
    incrementMonth(by) {
      var newDate = addMonths(this.currentDate(), by)
      store.dispatch('events/setFilterMonth', {
        coHostOrganizationId: this.organizationId,
        monthDate: newDate,
      })
      return newDate
    },
    openEventModal(oneEvent) {
      this.singleEvent = oneEvent
      // HACK: show url for the event /organization/:organizationId/events/:eventId
      window.history.pushState(
        {},
        null,
        window.location.pathname.replace(/\/events.*/, '') +
          '/events/' +
          oneEvent.id
      )
    },
    closeModal() {
      this.singleEvent = null
      window.history.back()
    },
    // Call only every 500ms so we don't update imediatelly if the user is browsing
    updateMonth: debounce(async function updateMonth(date) {
      await store.dispatch('events/setFilterMonth', {
        coHostOrganizationId: this.organizationId,
        monthDate: date,
      })
    }, 500),
  },
}
</script>

<template>
  <div v-infinite-scroll="nextPage" infinite-scroll-disabled="reachedLastPage">
    <div class="w-2/3 mt-7.7 pl-10 pr-12 relative">
      <EventBigCalendar
        :current-date="currentDate()"
        :events="allEvents || []"
        @filter-between="filterBetweenDates"
      >
      </EventBigCalendar>
    </div>
    <div class="h-auto mt-17 my-7.7">
      <p v-if="!events" class="px-11 post-content">Loading...</p>
      <p v-else-if="!events.length" class="px-11 post-content">
        There are no events that match these search criteria.
      </p>
      <div v-else class="flex events-grid">
        <div
          v-for="oneEvent in events"
          :key="oneEvent.id"
          class="event-box-wrapper"
        >
          <EventTile
            :one-event="oneEvent"
            @open-modal="openEventModal(oneEvent)"
          ></EventTile>
        </div>
      </div>
    </div>
    <div class="flex mb-7.7 pl-10 pr-12 relative justify-between">
      <div class="mr-4">
        <button
          class="text-lg text-blue-darker block focus:outline-none"
          @click="updateMonth(incrementMonth(-1))"
          >&lt; Back</button
        >
        <span class="text-sm text-blue-darker block">Previous</span>
      </div>
      <div>
        <button
          class="text-lg text-blue-darker block focus:outline-none"
          @click="updateMonth(incrementMonth(1))"
          >Next &gt;</button
        >
        <span class="text-sm text-blue-darker block">Upcoming</span>
      </div>
    </div>
    <EventDetails
      v-if="singleEvent"
      :one-event="singleEvent"
      @close="closeModal"
    ></EventDetails>
  </div>
</template>
